import { columnTypes } from '@/services/Grid/ColumnTypes';
import TooltipComponent from '@/components/Grid/Renderers/TooltipComponent';
import { cloneDeep, startCase, toLower } from 'lodash';
import ActionStatus from '@/services/Action/ActionStatus';
import ClientActionCategory from '@/services/Action/ClientActionCategory';

function CapitilizeValue({ value }) {
  return startCase(toLower(value));
}

/* Client Actions */
export const clientActionGridDefaultColDef = {
  filter: 'agSetColumnFilter',
  resizable: true,
  sortable: true,
  filterParams: {
    selectAllOnMiniFilter: false,
  },
};

export const clientActionGridColumnTypes = columnTypes;

const grid = [
  {
    headerName: 'ID',
    enablePivot: true,
    field: 'clientActionId',
    colId: 'clientActionId',
    width: 130,
    minWidth: 130,
    filter: 'agSetColumnFilter',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    sort: 'asc',
    aggFunc: 'count',
    enableValue: true,
  },
  {
    headerName: 'Linked Riskhub ID',
    field: 'linkedActionId',
    width: 180,
    minWidth: 180,
    enablePivot: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Action Due Date',
    enablePivot: true,
    field: 'dueDate',
    colId: 'dueDate',
    width: 150,
    minWidth: 150,
    type: 'dateColumn',
    enableRowGroup: true,
  },
  {
    headerName: 'Description',
    field: 'description',
    colId: 'description',
    width: 225,
    minWidth: 225,
    enablePivot: true,
    enableRowGroup: true,
    filter: 'agTextColumnFilter',
    cellRendererFramework: TooltipComponent,
  },
  {
    headerName: 'Global Status',
    enablePivot: true,
    field: 'status',
    colId: 'status',
    width: 175,
    minWidth: 175,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
    valueFormatter: params => CapitilizeValue(params),
    comparator: (valueA, valueB) => {
      //sort by the label of the status
      const valA = valueA ? valueA.toString().toLowerCase() : '';
      const valB = valueB ? valueB.toString().toLowerCase() : '';
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    },
    valueGetter: ({ data }) => {
      if (!data) { return ''; }

      const { status, secondaryStatus, completionStatus } = data;

      if (!secondaryStatus && !completionStatus) {
        return status;
      }

      const replacementStatus = completionStatus || secondaryStatus;
      const label = ActionStatus.getLabelForSecondaryOrCompletionStatus(replacementStatus);

      // If there is no label for it (such as 'full'), just return the normal status
      if (!label) {
        return status;
      }

      return label;
    },
    filterParams: {
      cellRenderer: CapitilizeValue,
    },
  },
  { // assessment action
    headerName: 'Priority',
    enablePivot: true,
    enableRowGroup: true,
    field: 'clientActionPriority',
    colId: 'clientActionPriority',
    width: 130,
    minWidth: 130,
  },
  {
    headerName: 'Type',
    enablePivot: true,
    enableRowGroup: true,
    field: 'clientActionType',
    colId: 'clientActionType',
    width: 130,
    minWidth: 130,
  },
  {
    headerName: 'RA Risk Rating',
    enablePivot: true,
    enableRowGroup: true,
    field: 'assessmentRiskRating',
    colId: 'assessmentRiskRating',
    width: 130,
    minWidth: 130,
    hide: true,
  },
  {
    headerName: 'User',
    enablePivot: true,
    field: 'reviewer',
    colId: 'reviewer',
    width: 200,
    minWidth: 200,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Team',
    enablePivot: true,
    field: 'currentResponsibleTeam',
    colId: 'currentResponsibleTeam',
    width: 225,
    minWidth: 225,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Last Submitted By',
    enablePivot: true,
    field: 'submittedBy',
    colId: 'submittedBy',
    width: 175,
    minWidth: 175,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Submitted for Approval By Team',
    enablePivot: true,
    field: 'submittedByTeam',
    colId: 'submittedByTeam',
    width: 250,
    minWidth: 250,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'UPRN',
    enablePivot: true,
    enableRowGroup: true,
    field: 'uprn',
    colId: 'uprn',
    width: 170,
    minWidth: 170,
  },
  {
    headerName: 'Parent UPRN',
    enablePivot: true,
    enableRowGroup: true,
    field: 'parentUprn',
    colId: 'parentUprn',
    width: 170,
    minWidth: 170,
  },
  {
    headerName: 'Hierarchy Level',
    enablePivot: true,
    enableRowGroup: true,
    field: 'propertyHierarchyType',
    colId: 'propertyHierarchyType',
    width: 170,
    minWidth: 170,
  },
  {
    headerName: 'Number Range',
    enablePivot: true,
    enableRowGroup: true,
    field: 'numberRange',
    colId: 'numberRange',
    width: 175,
    minWidth: 175,
  },
  {
    headerName: 'Building Name',
    enablePivot: true,
    enableRowGroup: true,
    field: 'buildingName',
    colId: 'buildingName',
    width: 200,
    minWidth: 200,
  }, {
    headerName: 'Unit Number',
    enablePivot: true,
    enableRowGroup: true,
    field: 'unitNumber',
    colId: 'unitNumber',
    width: 120,
    minWidth: 120,
  }, {
    headerName: 'Street Number',
    enablePivot: true,
    enableRowGroup: true,
    field: 'streetNumber',
    colId: 'streetNumber',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Street',
    enablePivot: true,
    enableRowGroup: true,
    field: 'street',
    colId: 'street',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Town',
    enablePivot: true,
    enableRowGroup: true,
    field: 'town',
    colId: 'town',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Postcode',
    enablePivot: true,
    enableRowGroup: true,
    field: 'postcode',
    colId: 'postcode',
    width: 125,
    minWidth: 125,
    filter: 'agTextColumnFilter',
  },


  {
    headerName: 'Property Type',
    enablePivot: true,
    enableRowGroup: true,
    field: 'propertyType',
    colId: 'propertyType',
    width: 125,
    minWidth: 125,
  }, {
    headerName: 'Tenure',
    enablePivot: true,
    enableRowGroup: true,
    field: 'tenure',
    colId: 'tenure',
    width: 125,
    minWidth: 125,
    cellRendererFramework: TooltipComponent,
  },
  {
    headerName: 'Tags',
    enablePivot: true,
    field: 'tags',
    colId: 'tags',
    width: 150,
    minWidth: 150,
    enableRowGroup: true,
  },
  {
    headerName: 'Created Date',
    enablePivot: true,
    enableRowGroup: true,
    field: 'createdDate',
    width: 170,
    minWidth: 170,
    type: 'dateColumn',
  },
  {
    headerName: 'Completed Date',
    enablePivot: true,
    enableRowGroup: true,
    field: 'completedDate',
    colId: 'completedDate',
    width: 170,
    minWidth: 170,
    type: 'dateColumn',
  },
  {
    headerName: 'Origin',
    enablePivot: true,
    enableRowGroup: true,
    field: 'origin',
    colId: 'origin',
    width: 125,
    minWidth: 125,
  },
  {
    headerName: 'Compliance Type',
    enablePivot: true,
    enableRowGroup: true,
    field: 'complianceType',
    colId: 'complianceType',
    width: 125,
    minWidth: 125,
  },
  {
    headerName: 'Min Quantity',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'minQuantity',
    colId: 'minQuantity',
    width: 165,
    minWidth: 165,
  }, {
    headerName: 'Max Quantity',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'maxQuantity',
    colId: 'maxQuantity',
    width: 165,
    minWidth: 165,
  }, {
    headerName: 'Recommendation',
    enablePivot: true,
    enableRowGroup: true,
    field: 'recommendation',
    colId: 'recommendation',
    width: 400,
    minWidth: 400,
    cellRendererFramework: TooltipComponent,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Specification',
    enablePivot: true,
    enableRowGroup: true,
    field: 'specification',
    colId: 'specification',
    width: 400,
    minWidth: 400,
    cellRendererFramework: TooltipComponent,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Rate',
    enablePivot: true,
    enableRowGroup: true,
    enableValue: true,
    field: 'rate',
    colId: 'rate',
    width: 110,
    minWidth: 110,
  }, {
    headerName: 'Min Cost',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'minCost',
    colId: 'minCost',
    width: 140,
    minWidth: 140,
  }, {
    headerName: 'Max Cost',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'maxCost',
    colId: 'maxCost',
    width: 140,
    minWidth: 140,
  }, {
    headerName: 'Work Order',
    enablePivot: true,
    enableRowGroup: true,
    field: 'workOrder',
    colId: 'workOrder',
    width: 150,
    minWidth: 150,
  }
];

for (let i = 0; i < ClientActionCategory.MAX_DEPTH(); i += 1) {
  const categoryKey = `clientActionCategory-${i}`;

  grid.push({
    headerName: `Category ${i + 1}`,
    enablePivot: true,
    enableRowGroup: true,
    field: categoryKey,
    colId: categoryKey,
    width: 125,
    minWidth: 125,
  });
}

export const clientActionGridColumns = grid;

export const queryGridColumns = [
  {
    headerName: 'Query With',
    enablePivot: true,
    enableRowGroup: true,
    field: 'queryUser',
    colId: 'queryUser',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
  },
];

export const getClientActionGridColumns = () => cloneDeep(clientActionGridColumns);

